/**
 * BottomTabs · RISO_FINAL_DESIGN nav primitive (mobile bottom navigation).
 * Per slot_riso_final_ds Phase A1.4 (15 MAY 2026) · ROLE lines 91-95.
 *
 * Mobile-only flat nav strip rendered as 5-item grid at bottom of viewport.
 * Used by NavShell (A1.5) when viewport width <=1100px.
 *
 * **Item set differs from Sidebar (desktop):**
 *   Sidebar (desktop · 8 items): 4 main + Glossary + Progress + Atelier + Buddy
 *   BottomTabs (mobile · 6 items): 4 main + Glossary + Progress
 *
 * Glossary added 17 MAY 2026 (slot_vocabulary_navbar) per Genie Q4=a · mobile
 * needs global access to the term reference too. Atelier and Buddy still
 * excluded for the reasons spelled out below.
 *
 * **Why drop Atelier and Buddy on mobile (per Genie pick, 15 MAY 2026):**
 *   - Atelier sub-app is not mobile-adapted; surfacing the button on phone
 *     would route students to a poor experience. Deep-link `/atelier` URL
 *     opens AtelierOverlay (A1.6) which shows a "лучше на десктопе" banner.
 *   - Buddy is a contextual helper accessible via Profile screen; persistent
 *     mobile nav slot would crowd the strip and matches no mobile use-case.
 *   - This 5-item set matches the prototype mobile ProductNav exactly and
 *     reuses the prototype CSS `grid-template-columns: repeat(5, 1fr)`
 *     verbatim (no CSS edit needed).
 *
 * Returns null when current screen is in HIDDEN_NAV_SCREENS (login/welcome/finaltest).
 *
 * CSS primitives consumed (see app/src/styles/globals.css migration block
 * "RISO FINAL DESIGN · nav primitives"):
 *   .bottom-tabs · .bottom-tabs button · .bt-ic · .bt-lab · button.active
 */

import { useState } from 'react';
import { NAV_ITEMS, NAV_FOOT_ITEMS, HIDDEN_NAV_SCREENS } from './Sidebar';
import { CourseLink } from './CourseLink';
import { navKeyToRoute } from '@/lib/courseRoute';

export interface BottomTabsProps {
  /** Current active screen — drives .active class via per-item match list. */
  screen: string;
  /** Click handler · emits item key (incl. 'atelier' но 'atelier' не в mobile items). */
  onNav: (key: string) => void;
}

/** Up-caret · marks a tab whose arrow opens a pop-up menu («Доска» → Домашка). */
function Caret() {
  return (
    <svg className="bt-caret" viewBox="0 0 12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7.5 L6 4.5 L9 7.5" />
    </svg>
  );
}

/**
 * Mobile items: 4 main + Glossary + Progress.
 * Atelier (mobile-unsuitable) and Buddy (contextual via Profile) excluded.
 * Order matters for muscle memory: matches Sidebar left-to-right order
 * (Glossary is the first foot item per Sidebar — kept first here too).
 */
const GLOSSARY_ITEM = NAV_FOOT_ITEMS.find((it) => it.k === 'glossary');
const PROGRESS_ITEM = NAV_FOOT_ITEMS.find((it) => it.k === 'progress');
// slot_homework_rework Ф1 (16 JUN 2026): «Практика» joined NAV_ITEMS (desktop top
// group under «Доска») but is filtered OUT of the mobile bar — the phone tab strip
// stays at 5 (no Samsung-Ultra crowding). Mobile homework entry lands в Ф1b via the
// «Доска» bottom-sheet.
// 9 JUL 2026: «Артель» переехала в NAV_ITEMS (десктоп-основная группа), но Артель —
// desktop-only (мобилка ведёт в оверлей-баннер, deep-link only), поэтому тоже вон из
// мобильного бара — как «Практика».
const MOBILE_ITEMS = [
  ...NAV_ITEMS.filter((it) => it.k !== 'practice' && it.k !== 'atelier'),
  ...(GLOSSARY_ITEM ? [GLOSSARY_ITEM] : []),
  ...(PROGRESS_ITEM ? [PROGRESS_ITEM] : []),
];

const isActive = (item: typeof NAV_ITEMS[number], screen: string): boolean =>
  item.match.includes(screen);

export function BottomTabs({ screen, onNav }: BottomTabsProps) {
  // slot_homework_rework Ф1b (17 JUN 2026 · Genie): the «Доска» tab opens a pop-up
  // menu (Тикеты · Домашка) instead of navigating straight to the board — that's the
  // mobile home for homework. Other tabs navigate directly.
  const [sheetItem, setSheetItem] = useState<(typeof NAV_ITEMS)[number] | null>(null);
  if (HIDDEN_NAV_SCREENS.has(screen)) return null;

  const childIsActive = (it: (typeof NAV_ITEMS)[number]) =>
    it.children?.some((c) => isActive(c, screen)) ?? false;

  // Pop-up menu items = the group's children minus the board-dup («Тикеты» == «Доска»),
  // i.e. just «Домашка». Keeps the menu one laconic panel (Genie 17 JUN).
  const menuItems = sheetItem?.children?.filter((c) => c.k !== sheetItem.k) ?? [];

  return (
    <>
      {sheetItem && menuItems.length > 0 && (
        <>
          <div className="bt-sheet-backdrop" role="presentation" onClick={() => setSheetItem(null)} />
          <div className="bt-sheet" role="menu" aria-label={sheetItem.label}>
            {menuItems.map((ch) => {
              const active = isActive(ch, screen);
              const route = navKeyToRoute(ch.k);
              const go = () => {
                setSheetItem(null);
                onNav(ch.k);
              };
              const inner = (
                <>
                  <span className="bt-sheet-ic">{ch.icon}</span>
                  {ch.label}
                </>
              );
              return route ? (
                <CourseLink
                  key={ch.k + ch.label}
                  route={route}
                  onNavigate={go}
                  role="menuitem"
                  className={`bt-sheet-item course-link${active ? ' active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  {inner}
                </CourseLink>
              ) : (
                <button
                  key={ch.k + ch.label}
                  type="button"
                  role="menuitem"
                  className={`bt-sheet-item${active ? ' active' : ''}`}
                  onClick={go}
                  aria-current={active ? 'page' : undefined}
                >
                  {inner}
                </button>
              );
            })}
          </div>
        </>
      )}
      <nav className="bottom-tabs" aria-label="Навигация">
        {MOBILE_ITEMS.map((it) => {
          const hasMenu = !!it.children && it.children.length > 0;
          const active = isActive(it, screen) || (hasMenu && childIsActive(it));
          const isOpen = sheetItem?.k === it.k;

          const route = navKeyToRoute(it.k);
          const tabInner = (
            <>
              <span className="bt-ic">{it.icon}</span>
              <span className="bt-lab">{it.label}</span>
            </>
          );

          if (hasMenu) {
            // «Доска» tab: the main link navigates to the board (open-in-new-tab
            // via the <a>); the separate caret button opens the «Домашка» pop-up.
            return (
              <div key={it.k} className="bt-cell">
                {route ? (
                  <CourseLink
                    route={route}
                    onNavigate={() => onNav(it.k)}
                    className={`bt-main course-link${active ? ' active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    aria-label={it.label}
                  >
                    {tabInner}
                  </CourseLink>
                ) : (
                  <button
                    type="button"
                    className={`bt-main${active ? ' active' : ''}`}
                    onClick={() => onNav(it.k)}
                    aria-current={active ? 'page' : undefined}
                    aria-label={it.label}
                  >
                    {tabInner}
                  </button>
                )}
                <button
                  type="button"
                  className={`bt-caret-btn${isOpen ? ' bt-open' : ''}`}
                  onClick={() => setSheetItem(isOpen ? null : it)}
                  aria-label={isOpen ? 'Скрыть меню доски' : 'Показать Домашку'}
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                >
                  <Caret />
                </button>
              </div>
            );
          }

          return route ? (
            <CourseLink
              key={it.k}
              route={route}
              onNavigate={() => onNav(it.k)}
              className={`course-link${active ? ' active' : ''}`}
              aria-current={active ? 'page' : undefined}
              aria-label={it.label}
            >
              {tabInner}
            </CourseLink>
          ) : (
            <button
              key={it.k}
              type="button"
              className={active ? 'active' : ''}
              onClick={() => onNav(it.k)}
              aria-current={active ? 'page' : undefined}
              aria-label={it.label}
            >
              {tabInner}
            </button>
          );
        })}
      </nav>
    </>
  );
}

/** Exposed for tests · count must match grid-template-columns in CSS. */
export const MOBILE_ITEM_COUNT = MOBILE_ITEMS.length;
